import * as THREE from 'three'
import {
    trackGeoStartLength,
    trackRegionConfig,
} from '../../config/constants'
import { applyHeightFogToObject } from '../../materials/heightFog'
import type { TrackController } from './trackController'
import type { TrackPath } from './trackImporter'
import {
    resolveTrackRegionConfig,
    type ResolvedTrackRegionConfig,
} from './trackRegions'
import { markForDisposal } from '../../../utils/threeDisposal'

type SourceTrackPart = {
    geometry: THREE.BufferGeometry
    material: THREE.MeshBasicMaterial
}

type TrackTextures = {
    region1: THREE.Texture
    region2: THREE.Texture
    region3: THREE.Texture
    region4: THREE.Texture
    transition: THREE.Texture
}

type TrackMeshBucket = {
    region1: THREE.BufferGeometry
    region2: THREE.BufferGeometry
    region3: THREE.BufferGeometry
    region4: THREE.BufferGeometry
    transition1: THREE.BufferGeometry
    transition2: THREE.BufferGeometry
    transition3: THREE.BufferGeometry
}

type TrackTileVariant =
    | 0
    | 1
    | 2
    | 3
    | 4
    | 5
    | 6

export function createTrackGeo(
    trackAsset: THREE.Group,
    trackTextures: TrackTextures,
    trackController: TrackController,
): THREE.Group {
    const trackPart = collectSourceTrack(trackAsset)
    const trackMaterials = createTrackMaterials(trackTextures)
    const trackGeoGroup = buildTrackMesh(trackPart, trackMaterials, trackController)
    trackPart.geometry.dispose()
    return trackGeoGroup
}

export function applyTrackMeshEndDistance(
    trackPath: TrackPath,
    trackAsset: THREE.Group,
): TrackPath {
    const trackPart = collectSourceTrack(trackAsset)

    if (!trackPart.geometry.boundingBox) {
        throw new Error('Track mesh is missing its bounding box.')
    }

    const bounds = trackPart.geometry.boundingBox
    const tileLength = bounds.max.z - bounds.min.z
    const usableTrackLength = Math.max(trackPath.totalLength - trackGeoStartLength, 0)
    const tileCount = Math.max(1, Math.floor(usableTrackLength / tileLength))
    const meshEndDistance = Math.min(
        trackPath.totalLength,
        trackGeoStartLength + tileCount * tileLength,
    )
    const resolvedRegionConfig = snapTrackRegionsToMeshTiles(
        trackPath,
        tileLength,
        tileCount,
        meshEndDistance,
    )

    if (
        meshEndDistance === trackPath.pathEndDistance &&
        hasMatchingResolvedRegions(
            trackPath.resolvedRegionConfig,
            resolvedRegionConfig,
        )
    ) {
        return trackPath
    }

    return {
        ...trackPath,
        pathEndDistance: meshEndDistance,
        resolvedRegionConfig,
    }
}

function snapTrackRegionsToMeshTiles(
    trackPath: TrackPath,
    tileLength: number,
    tileCount: number,
    trackLength: number,
): ResolvedTrackRegionConfig {
    const sourceRegionConfig = resolveTrackRegionConfig(
        trackRegionConfig,
        trackPath,
    )

    return {
        transition1: snapTransitionRangeToMeshTiles(
            sourceRegionConfig.transition1,
            tileLength,
            tileCount,
            trackLength,
        ),
        transition2: snapTransitionRangeToMeshTiles(
            sourceRegionConfig.transition2,
            tileLength,
            tileCount,
            trackLength,
        ),
        transition3: snapTransitionRangeToMeshTiles(
            sourceRegionConfig.transition3,
            tileLength,
            tileCount,
            trackLength,
        ),
    }
}

function snapTransitionRangeToMeshTiles(
    transition: ResolvedTrackRegionConfig['transition1'],
    tileLength: number,
    tileCount: number,
    trackLength: number,
): ResolvedTrackRegionConfig['transition1'] {
    let firstTileStart: number | null = null
    let lastTileEnd: number | null = null

    for (let tileIndex = 0; tileIndex < tileCount; tileIndex += 1) {
        const tileStart = trackGeoStartLength + tileIndex * tileLength
        const tileMidpoint = tileStart + tileLength * 0.5

        if (
            tileMidpoint < transition.start ||
            tileMidpoint > transition.end
        ) {
            continue
        }

        firstTileStart ??= tileStart
        lastTileEnd = tileStart + tileLength
    }

    if (firstTileStart === null || lastTileEnd === null) {
        return {
            start: THREE.MathUtils.clamp(transition.start, 0, trackLength),
            end: THREE.MathUtils.clamp(transition.end, 0, trackLength),
        }
    }

    return {
        start: THREE.MathUtils.clamp(firstTileStart, 0, trackLength),
        end: THREE.MathUtils.clamp(lastTileEnd, 0, trackLength),
    }
}

function hasMatchingResolvedRegions(
    current: ResolvedTrackRegionConfig | undefined,
    next: ResolvedTrackRegionConfig,
): boolean {
    if (!current) {
        return false
    }

    return (
        current.transition1.start === next.transition1.start &&
        current.transition1.end === next.transition1.end &&
        current.transition2.start === next.transition2.start &&
        current.transition2.end === next.transition2.end &&
        current.transition3.start === next.transition3.start &&
        current.transition3.end === next.transition3.end
    )
}

function collectSourceTrack(trackAsset: THREE.Group): SourceTrackPart {
    let track: SourceTrackPart | null = null
    trackAsset.updateMatrixWorld(true)
    trackAsset.traverse((child) => {
        if (!(child instanceof THREE.Mesh)) {
            return
        }
        const geometry = child.geometry.index ? child.geometry.toNonIndexed() : child.geometry.clone()
        geometry.applyMatrix4(child.matrixWorld)
        geometry.computeBoundingBox()
        track = { geometry, material: child.material }
    })
    if (!track) {
        throw new Error('Track asset needs at least one mesh.')
    }
    return track
}

function createTrackMaterials(sourceTextures: TrackTextures): THREE.MeshBasicMaterial[] {
    const materials: THREE.MeshBasicMaterial[] = [
        new THREE.MeshBasicMaterial(),
        new THREE.MeshBasicMaterial(),
        new THREE.MeshBasicMaterial(),
        new THREE.MeshBasicMaterial(),
        new THREE.MeshBasicMaterial(),
    ]
    materials.forEach(markForDisposal)
    materials[0].map = sourceTextures.region1
    materials[1].map = sourceTextures.region2
    materials[2].map = sourceTextures.region3
    materials[3].map = sourceTextures.region4
    materials[4].map = sourceTextures.transition
    return materials
}

function buildTrackMesh(
    part: SourceTrackPart,
    trackMaterials: THREE.MeshBasicMaterial[],
    trackController: TrackController,
): THREE.Group {
    if (!part.geometry.boundingBox) {
        throw new Error('Track mesh is missing its bounding box.')
    }
    const bounds = part.geometry.boundingBox
    const tileLength = bounds.max.z - bounds.min.z
    const usableTrackLength = Math.max(
        trackController.getTrackLength() - trackGeoStartLength,
        0,
    )
    const tileCount = Math.max(1, Math.floor(usableTrackLength / tileLength))
    const geometries = createTrackGeometry(
        part.geometry,
        bounds.min.z,
        tileLength,
        tileCount,
        trackController,
    )

    const trackGeoGroup = new THREE.Group()
    const meshEntries: Array<[
        name: string,
        geometry: THREE.BufferGeometry,
        material: THREE.MeshBasicMaterial,
    ]> = [
        ['TrackRegion1', geometries.region1, trackMaterials[0]],
        ['TrackRegion2', geometries.region2, trackMaterials[1]],
        ['TrackRegion3', geometries.region3, trackMaterials[2]],
        ['TrackRegion4', geometries.region4, trackMaterials[3]],
        ['TrackTransition1', geometries.transition1, trackMaterials[4]],
        ['TrackTransition2', geometries.transition2, trackMaterials[4]],
        ['TrackTransition3', geometries.transition3, trackMaterials[4]],
    ]

    for (const [name, geometry, material] of meshEntries) {
        if (geometry.getAttribute('position').count === 0) {
            continue
        }

        const mesh = new THREE.Mesh(geometry, material)
        mesh.name = name
        mesh.castShadow = false
        mesh.receiveShadow = false
        trackGeoGroup.add(mesh)
    }

    applyHeightFogToObject(trackGeoGroup)

    return trackGeoGroup
}

function createTrackGeometry(
    source: THREE.BufferGeometry,
    sourceMinZ: number,
    tileLength: number,
    tileCount: number,
    trackController: TrackController,
): TrackMeshBucket {
    const positionAttribute = source.getAttribute('position')
    const normalAttribute = source.getAttribute('normal')
    const uvAttribute = source.getAttribute('uv')

    const bucketOrder = [
        'region1',
        'region2',
        'region3',
        'region4',
        'transition1',
        'transition2',
        'transition3',
    ] as const
    const positionsByBucket = new Map<
        typeof bucketOrder[number],
        number[]
    >()
    const normalsByBucket = new Map<
        typeof bucketOrder[number],
        number[]
    >()
    const uvsByBucket = new Map<
        typeof bucketOrder[number],
        number[]
    >()

    for (const bucket of bucketOrder) {
        positionsByBucket.set(bucket, [])
        normalsByBucket.set(bucket, [])
        uvsByBucket.set(bucket, [])
    }

    const localPosition = new THREE.Vector3()
    const localNormal = new THREE.Vector3()
    const worldPosition = new THREE.Vector3()
    const worldNormal = new THREE.Vector3()

    for (let tileIndex = 0; tileIndex < tileCount; tileIndex += 1) {
        const tileDistance = trackGeoStartLength + tileIndex * tileLength
        const tileVariant = resolveTrackMaterialVariant(
            tileIndex,
            tileLength,
            trackController,
        )
        const bucket = resolveTrackBucketName(tileVariant)
        const bucketPositions = positionsByBucket.get(bucket)!
        const bucketNormals = normalsByBucket.get(bucket)!
        const bucketUvs = uvsByBucket.get(bucket)!

        for (let vertexIndex = 0; vertexIndex < positionAttribute.count; vertexIndex += 1) {
            localPosition.fromBufferAttribute(positionAttribute, vertexIndex)

            const frame = trackController.frameAtPosition(
                tileDistance + localPosition.z - sourceMinZ,
            )

            worldPosition
                .copy(frame.position)
                .addScaledVector(frame.trackRight, localPosition.x)
                .addScaledVector(frame.up, localPosition.y)

            bucketPositions.push(
                worldPosition.x,
                worldPosition.y,
                worldPosition.z,
            )

            localNormal.fromBufferAttribute(normalAttribute, vertexIndex).normalize()

            worldNormal
                .copy(frame.trackRight)
                .multiplyScalar(localNormal.x)
                .addScaledVector(frame.up, localNormal.y)
                .addScaledVector(frame.tangent, localNormal.z)
                .normalize()

            bucketNormals.push(
                worldNormal.x,
                worldNormal.y,
                worldNormal.z,
            )

            bucketUvs.push(
                uvAttribute.getX(vertexIndex),
                uvAttribute.getY(vertexIndex) + tileIndex,
            )
        }
    }

    return {
        region1: createTrackBucketGeometry(
            positionsByBucket.get('region1')!,
            normalsByBucket.get('region1')!,
            uvsByBucket.get('region1')!,
        ),
        region2: createTrackBucketGeometry(
            positionsByBucket.get('region2')!,
            normalsByBucket.get('region2')!,
            uvsByBucket.get('region2')!,
        ),
        region3: createTrackBucketGeometry(
            positionsByBucket.get('region3')!,
            normalsByBucket.get('region3')!,
            uvsByBucket.get('region3')!,
        ),
        region4: createTrackBucketGeometry(
            positionsByBucket.get('region4')!,
            normalsByBucket.get('region4')!,
            uvsByBucket.get('region4')!,
        ),
        transition1: createTrackBucketGeometry(
            positionsByBucket.get('transition1')!,
            normalsByBucket.get('transition1')!,
            uvsByBucket.get('transition1')!,
        ),
        transition2: createTrackBucketGeometry(
            positionsByBucket.get('transition2')!,
            normalsByBucket.get('transition2')!,
            uvsByBucket.get('transition2')!,
        ),
        transition3: createTrackBucketGeometry(
            positionsByBucket.get('transition3')!,
            normalsByBucket.get('transition3')!,
            uvsByBucket.get('transition3')!,
        ),
    }
}

function resolveTrackMaterialVariant(
    tileIndex: number,
    tileLength: number,
    trackController: TrackController,
): TrackTileVariant {
    const samplePosition =
        trackGeoStartLength + tileIndex * tileLength + tileLength * 0.5
    const regionState = trackController.getTrackRegionState(samplePosition)
    if (!regionState.activeTransition) {
        return (regionState.activeRegion - 1) as 0 | 1 | 2 | 3
    }

    switch (regionState.activeRegion) {
        case 1:
            return 4
        case 2:
            return 5
        case 3:
            return 6
        case 4:
            return 3
    }
}

function createTrackBucketGeometry(
    positions: number[],
    normals: number[],
    uvs: number[],
) {
    const geometry = new THREE.BufferGeometry()
    markForDisposal(geometry)
    geometry.setAttribute(
        'position',
        new THREE.BufferAttribute(new Float32Array(positions), 3),
    )
    geometry.setAttribute(
        'normal',
        new THREE.BufferAttribute(new Float32Array(normals), 3),
    )
    geometry.setAttribute(
        'uv',
        new THREE.BufferAttribute(new Float32Array(uvs), 2),
    )
    return geometry
}

function resolveTrackBucketName(tileVariant: TrackTileVariant) {
    switch (tileVariant) {
        case 0:
            return 'region1' as const
        case 1:
            return 'region2' as const
        case 2:
            return 'region3' as const
        case 3:
            return 'region4' as const
        case 4:
            return 'transition1' as const
        case 5:
            return 'transition2' as const
        case 6:
            return 'transition3' as const
        default:
            throw new Error(`Unknown track tile variant: ${tileVariant}`)
    }
}
